import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Box,
  Divider,
} from '@mui/material'
import { Trans, msg } from '@lingui/macro'
import { useLingui } from '@lingui/react'

function weightLabel(value, fallback) {
  if (value == null || value === '') return fallback
  return String(value)
}

/**
 * Explains the road risk score formula (computed per meter, shown per km).
 */
export default function RoadRiskMapFormulaDialog({ open, onClose, settings }) {
  const { _ } = useLingui()
  const s = settings || {}

  const wF = weightLabel(s.weightFatal, '10')
  const wSi = weightLabel(s.weightSerious, '5')
  const wLi = weightLabel(s.weightSlight, '2')
  const wNi = weightLabel(s.weightNoInjury, '1')
  const wUk = weightLabel(s.weightUnknown, '1')
  const minDenom = s.minDenominatorLengthM != null ? String(s.minDenominatorLengthM) : '400'

  const mono = {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontSize: 13,
    lineHeight: 1.55,
    whiteSpace: 'pre-wrap',
    bgcolor: 'action.hover',
    borderRadius: 1,
    px: 1.5,
    py: 1.25,
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Trans>Risk score formula</Trans>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            <Trans>
              The score is computed per metre of road, then shown per kilometre (×1000).
            </Trans>
          </Typography>

          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {_(msg`Denominator`)}
          </Typography>
          <Box component="pre" sx={mono}>
{`length_m
denominator_m = MAX(length_m, min_denominator_length_m)
                (default min_denominator_length_m = ${minDenom} m)`}
          </Box>

          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {_(msg`Score (per metre)`)}
          </Typography>
          <Box component="pre" sx={mono}>
{`riskScore_m =
  (${wF}·F + ${wSi}·SI + ${wLi}·LI + ${wNi}·NI + ${wUk}·UK)
  / denominator_m`}
          </Box>

          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {_(msg`Display (per kilometre)`)}
          </Typography>
          <Box component="pre" sx={mono}>
{`riskScore_km = riskScore_m × 1000`}
          </Box>

          <Divider />

          <Typography variant="body2">
            <Trans>F</Trans> = <Trans>Fatal</Trans>
            {' · '}
            <Trans>SI</Trans> = <Trans>Serious</Trans>
            {' · '}
            <Trans>LI</Trans> = <Trans>Slight</Trans>
          </Typography>
          <Typography variant="body2">
            <Trans>NI</Trans> = <Trans>No injury</Trans>
            {' · '}
            <Trans>UK</Trans> = <Trans>Unknown</Trans>
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          <Trans>Close</Trans>
        </Button>
      </DialogActions>
    </Dialog>
  )
}
